import type { EventName, EventPayloads } from "./mixpanel_generated/events";
import { EVENT_PROPERTY_SCHEMA } from "./mixpanel_generated/event_properties";
import { getProvider } from "./registry";
import { isAnalyticsDebug } from "./debug";

/**
 * The single, typed entry point for firing a Mixpanel event. `props` is constrained to the
 * generated payload for `name`, so wrong/missing/mistyped properties are TypeScript errors.
 * Always pass an object (stub events take `{}`).
 */
export function trackEvent<E extends EventName>(name: E, props: EventPayloads[E]): void {
  // Drop any event the generated plan doesn't define — e.g. an event removed from the CSVs but
  // still referenced by orphaned/stale instrumentation. `name` is typed as `EventName`, so this
  // can only be false at runtime after types are stripped; dropping prevents polluting Mixpanel
  // with ad-hoc event names. (Compile-time, `yarn typecheck` flags such call sites.)
  if (!Object.prototype.hasOwnProperty.call(EVENT_PROPERTY_SCHEMA, name)) {
    if (isAnalyticsDebug()) {
      console.warn(`[analytics] dropping unknown event "${name}" — not in the generated tracking plan`);
    }
    return;
  }
  if (isAnalyticsDebug()) {
    validatePayload(name, props as Record<string, unknown>);
  }
  getProvider()?.track(name, props);
}

function validatePayload(name: EventName, props: Record<string, unknown>): void {
  const schema = EVENT_PROPERTY_SCHEMA[name];
  if (!schema) {
    console.warn(`[analytics] unknown event "${name}"`);
    return;
  }
  for (const [key, desc] of Object.entries(schema)) {
    if (!desc.optional && !(key in props)) {
      console.warn(`[analytics] ${name}: missing required prop "${key}"`);
    }
  }
  for (const key of Object.keys(props)) {
    const desc = schema[key];
    if (!desc) {
      console.warn(`[analytics] ${name}: unknown prop "${key}"`);
      continue;
    }
    const v = props[key];
    if (v === undefined || v === null) continue;
    const ok =
      desc.type === "list"
        ? Array.isArray(v)
        : desc.type === "number"
          ? typeof v === "number"
          : desc.type === "boolean"
            ? typeof v === "boolean"
            : typeof v === "string";
    if (!ok) {
      console.warn(`[analytics] ${name}: prop "${key}" expected ${desc.type}, got ${typeof v}`);
    }
  }
}
